import * as React from 'react';
import { Component } from 'react';
import propz from 'propz';
import moment from 'moment';
import { loadStripe } from '@stripe/stripe-js';
import { MessageField } from 'Src/models/message';
import { EventInvitationMessage } from 'Src/models/message';
import { CLUB_PARTICIPATION_MESSAGE_INVITATION_STATUS } from 'Src/consts/message';
import { CLUB_MESSAGE_INVITE_STATUS_ALL } from 'Src/consts/club';
import { WEEK_DAYS_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/date';
import { isNumeric } from 'Src/helpers/common/common';
import { SECONDS_IN_MINUTE, MILLISECONDS_IN_SECOND } from 'Src/consts/common';
import { CLUB_PRICING } from 'Src/consts/club';
import { Map } from 'Src/components/Map/Map';
import { Select } from 'Src/components/Select/Select';
import {
  getSelectOptionsForClubPriority,
  getSelectOptionForBooleanQuestion,
  getSelectOptionForClubInvitationEnumQuestion
} from 'Src/helpers/table/select';
import { SelectOption } from 'Src/helpers/table/table';
import { createPayment, sentAnswers } from 'Src/helpers/service/parent/messagesTable';
import { QUESTIONS_TEMPLATE_TYPE } from 'Src/consts/message';
import { getAvailableClubSpaces } from 'Src/helpers/service/parent/messagesTable';
import { convertStringToBoolean } from 'Src/helpers/common/common';
import './ClubInvitationForm.scss';
import { FormDefinition, FormSubmission } from 'Src/types/formSchema';
import { FormRendererV2 } from 'Src/components/FormRendererV2/FormRendererV2';
import {
  applyAnswersToLegacyFields,
  formDefinitionToLegacyFields,
  legacyFieldsToAnswers,
  legacyFieldsToFormDefinition
} from 'Src/helpers/form/legacyFormAdapter';
import {
  FormAnswerMap,
  FormErrorMap,
  answerMapToArray,
  answersToMap,
  validateFormDefinition
} from 'Src/helpers/form/formRuntime';
import { getSubmissionElementIds, hasFormFlow } from 'Src/helpers/form/formFlowRuntime';
import { uploadFormFilesForElement } from 'Src/helpers/form/formMedia';
import { formatAgesCompactForSchool } from 'Src/helpers/ageGroups';

interface Props {
  user: any;
  message: EventInvitationMessage;
  onBookNowClick: (fields: MessageField[], priority: number, submission?: FormSubmission) => void;
  closeClubInvitationModal: () => void;
  onAddToWaitingListClick: () => void;
  onAddToBasketClick?: () => void;
  onDeclineClick?: () => void;
}

interface State {
  fields: MessageField[];
  isMapVisible: boolean;
  activePriorityIndex: number;
  selectOptionsForClubPriority: SelectOption[];
  activeSelectIndex: number[];
  errors: Error[];
  isWaitingListInfoVisible: boolean;
  noClubSpacesInfoVisible: boolean;
  definition: FormDefinition;
  answers: FormAnswerMap;
  formErrors: FormErrorMap;
}

interface Error {
  isError: boolean;
  errorText: string;
}

const toClubEndMoment = (msg: EventInvitationMessage): moment.Moment | null => {
  const values: any[] = [propz.get(msg, ['clubData', 'finishDate'])];
  const dates = propz.get(msg, ['clubData', 'dates'], []);
  const intervals = propz.get(msg, ['clubData', 'intervals'], []);
  if (Array.isArray(dates)) values.push(...dates);
  if (Array.isArray(intervals)) values.push(...intervals.map(interval => propz.get(interval, ['end'])));
  const ends = values.map(value => moment(value).endOf('day')).filter(value => value.isValid());
  return ends.length ? moment.max(ends) : null;
};

const buildClubSummary = (msg: EventInvitationMessage) => {
  const clubName = propz.get(msg, ['clubData', 'name'], '');
  const placeName = propz.get(msg, ['clubData', 'venue', 'placeData', 'name'], '');
  const postcode = propz.get(msg, ['clubData', 'venue', 'placeData', 'postcode'], '');
  const venue = placeName || postcode || '';

  const startDate = propz.get(msg, ['clubData', 'startDate']);
  const endDate = propz.get(msg, ['clubData', 'finishDate']);
  const start = startDate ? moment(startDate) : null;
  const end = endDate ? moment(endDate) : null;

  const runRange =
    start && end && start.isValid() && end.isValid()
      ? `${start.format('DD MMM YYYY')} – ${end.format('DD MMM YYYY')}`
      : start?.isValid()
      ? start.format('DD MMM YYYY')
      : '';

  const startTimeISO = propz.get(msg, ['clubData', 'time']);
  const durationMin = propz.get(msg, ['clubData', 'duration'], 0);
  const startTs = startTimeISO ? new Date(startTimeISO).getTime() : undefined;
  const endTs = startTs ? startTs + durationMin * SECONDS_IN_MINUTE * MILLISECONDS_IN_SECOND : undefined;
  const timeRange = startTs ? `${moment(startTs).format('HH:mm')}–${moment(endTs).format('HH:mm')}` : '';

  const weekDays = (propz.get(msg, ['clubData', 'days'], []) as string[])
    .map(d => WEEK_DAYS_SERVER_TO_CLIENT_MAPPING[d] || d)
    .join(', ');

  const when = weekDays ? `${weekDays} · ${timeRange}` : timeRange;

  return { clubName, venue, runRange, when };
};

export class ClubInvitationForm extends Component<Props, State> {
  constructor(props) {
    super(props);
    const { message } = this.props;
    const selectOptionsForClubPriority = getSelectOptionsForClubPriority();
    const selectOptionForBooleanQuestion = getSelectOptionForBooleanQuestion();
    const fields = propz.get(message, ['fields']);

    let fieldsUpdated;
    let activeSelectIndex = [];
    let errors = [];

    const isFieldsExist = typeof fields !== 'undefined';
    const isV2 = message.formSchemaVersion === 2 && !!message.formDefinition;
    const definition = isV2 ? message.formDefinition : legacyFieldsToFormDefinition(isFieldsExist ? fields : []);
    if (isFieldsExist) {
      fieldsUpdated = fields.map((field, index) => {
        const isValueExist = typeof field.value !== 'undefined' && field.value !== '';

        switch (true) {
          case field.type === QUESTIONS_TEMPLATE_TYPE.BOOLEAN:
            activeSelectIndex[index] = isValueExist
              ? selectOptionForBooleanQuestion.findIndex(item => item.value === field.value.toString())
              : 0;
            break;

          case field.type === QUESTIONS_TEMPLATE_TYPE.ENUM:
            activeSelectIndex[index] = isValueExist ? field.enumOptions.findIndex(item => item === field.value) : 0;
            break;

          default:
            activeSelectIndex[index] = 0;
            break;
        }

        errors[index] = { isError: false, errorText: '' };

        return {
          ...field,
          value: isValueExist ? field.value : ''
        };
      });
    }

    this.state = {
      fields: isFieldsExist ? fieldsUpdated : [],
      isMapVisible: false,
      activePriorityIndex: 0,
      activeSelectIndex: isFieldsExist ? activeSelectIndex : [],
      errors: isFieldsExist ? errors : [],
      selectOptionsForClubPriority: selectOptionsForClubPriority,
      isWaitingListInfoVisible: false,
      noClubSpacesInfoVisible: true,
      definition,
      answers: answersToMap(
        isV2 ? message.formAnswers || [] : legacyFieldsToAnswers(isFieldsExist ? fields : [], definition)
      ),
      formErrors: {}
    };
  }

  pay = () => {
    const { message, user } = this.props;

    let sessionId;
    let publicKey;

    const cancelUrl = document.location.href;
    const successUrl = `${document.location.origin}/paymentSuccess`;

    const data = { successUrl, cancelUrl };

    createPayment(user, message.id, data)
      .then(session => {
        sessionId = session.id;
        publicKey = session.publicKey;
      })
      .then(result => {
        return loadStripe(publicKey);
      })
      .then(stripe => {
        return stripe.redirectToCheckout({
          sessionId: sessionId
        });
      })
      .then(result => {
        if (result.error) {
          alert(result.error.message);
        }
      })
      .catch(err => {
        if (typeof err !== 'undefined') {
          //  TODO: for prod replace on  "contact support"
          const displayMessageText = propz.get(err, ['response', 'data', 'details', 'text']);
          console.log(err);
          alert(`Error: ${displayMessageText}`);
        }
      });
  };

  onSubmit = (isAddToBasket: boolean) => {
    const { fields, errors, selectOptionsForClubPriority, activePriorityIndex, definition, answers } = this.state;
    const { message, user, onAddToBasketClick } = this.props;
    const askToPay = propz.get<Boolean>(message, ['clubData', 'paymentSettings', 'askToPay'], false);
    const isBookingFromWaitingList = propz.get(message, ['clubData', 'isBookingFromWaitingList'], false);
    const invitationStatus = propz.get(message, ['invitationStatus']);

    const isV2 = message.formSchemaVersion === 2 && !!message.formDefinition;
    const v2Errors = isV2
      ? validateFormDefinition(definition, answers, getSubmissionElementIds(definition, answers))
      : {};
    let submitAbility = Object.keys(v2Errors).length === 0;
    let fieldsUpdated = [...fields];
    let submission: FormSubmission | undefined;
    if (isV2) {
      const legacyFields = formDefinitionToLegacyFields(definition);
      fieldsUpdated = applyAnswersToLegacyFields(legacyFields, definition, answerMapToArray(answers)) as MessageField[];
      submission = {
        formSchemaVersion: 2,
        formAnswers: answerMapToArray(answers)
      };
    }
    const priority = Number(selectOptionsForClubPriority[activePriorityIndex].value);

    const errorsUpdated = errors.map(item => {
      return {
        isError: false,
        errorText: ''
      };
    });

    !isV2 &&
      fields.forEach((field, index) => {
        const isNumericField = isNumeric(field.value);
        const isNumberType = field.type === QUESTIONS_TEMPLATE_TYPE.NUMBER;
        const isBooleanType = field.type === QUESTIONS_TEMPLATE_TYPE.BOOLEAN;
        const isEnumType = field.type === QUESTIONS_TEMPLATE_TYPE.ENUM;
        const isFieldEmpty = field.value === '';

        if (isBooleanType) {
          let value;

          if (isFieldEmpty) {
            const options = getSelectOptionForBooleanQuestion();
            value = options[0].value;
          } else {
            value = propz.get(field, ['value']);
          }

          const booleanValue = convertStringToBoolean(value);
          propz.set(fieldsUpdated[index], ['value'], booleanValue);
        }

        if (isEnumType && isFieldEmpty) {
          const options = getSelectOptionForClubInvitationEnumQuestion(field.enumOptions);
          const value = options[0].value;
          propz.set(fieldsUpdated[index], ['value'], value);
        }

        if (isNumberType && !isNumericField && !isFieldEmpty) {
          errorsUpdated[index].isError = true;
          errorsUpdated[index].errorText = 'please enter a number';
          submitAbility = false;
        }

        const isFieldUpdatedEmpty = fieldsUpdated[index].value === '';

        if (isFieldUpdatedEmpty && field.isRequired) {
          errorsUpdated[index].isError = true;
          errorsUpdated[index].errorText = 'required';
          submitAbility = false;
        }
      });

    switch (true) {
      case submitAbility && askToPay:
        getAvailableClubSpaces(user, message.id).then(countObj => {
          const { availableParticipants } = countObj;

          const isSpacesAvailable = availableParticipants > 0;

          const isInviteStatusPending = invitationStatus === CLUB_MESSAGE_INVITE_STATUS_ALL.PENDING;
          const isInviteStatusPendingAndAvailableForBooking =
            isInviteStatusPending && !isBookingFromWaitingList && isSpacesAvailable;

          const isInviteStatusWaitingListCanBook =
            invitationStatus === CLUB_MESSAGE_INVITE_STATUS_ALL.WAITING_LIST_CAN_BOOK;
          const isInviteStatusWaitingCanBookAndAvailableForBooking =
            isInviteStatusWaitingListCanBook && isBookingFromWaitingList && isSpacesAvailable;

          const isBookingAvailable =
            isInviteStatusPendingAndAvailableForBooking || isInviteStatusWaitingCanBookAndAvailableForBooking;

          const data = { fields: fieldsUpdated, priority, ...(submission || {}) };

          sentAnswers(user, message.id, data);

          if (isBookingAvailable) {
            isAddToBasket ? onAddToBasketClick() : this.pay();
          } else {
            isInviteStatusWaitingListCanBook
              ? this.setState({
                  noClubSpacesInfoVisible: false
                })
              : this.setState({
                  isWaitingListInfoVisible: true
                });
          }
        });

        break;

      case submitAbility && !askToPay:
        this.props.onBookNowClick(fieldsUpdated, priority, submission);

        break;

      case !submitAbility:
        this.setState({ errors: errorsUpdated, formErrors: v2Errors });

        break;
    }
  };

  onAnswersChange = (answers: FormAnswerMap) => {
    this.setState(prev => {
      const formErrors = { ...prev.formErrors };
      Object.keys(answers).forEach(id => {
        if (JSON.stringify(answers[id]) !== JSON.stringify(prev.answers[id])) delete formErrors[id];
      });
      return { answers, formErrors };
    });
  };

  getAges(): string {
    const { message, user } = this.props;
    const ages = propz.get(message, ['clubData', 'ages']);
    if (typeof ages === 'undefined') return '';

    return formatAgesCompactForSchool(ages, propz.get(message, ['schoolData'], user.activeSchool));
  }

  onPrioritySelect = (value: string): void => {
    const { selectOptionsForClubPriority } = this.state;
    const nextIndex = selectOptionsForClubPriority.findIndex(priority => Number(priority.value) === Number(value));
    this.setState({
      activePriorityIndex: nextIndex
    });
  };

  renderActionButtons = () => {
    const { message } = this.props;
    const { isWaitingListInfoVisible } = this.state;

    const askToPay = propz.get(message, ['clubData', 'paymentSettings', 'askToPay'], false);

    return (
      <>
        {isWaitingListInfoVisible ? (
          <button onClick={this.props.onAddToWaitingListClick} className="btn btn-respond btn-accept mr-2">
            Add to waiting list
          </button>
        ) : (
          <>
            <button onClick={() => this.onSubmit(false)} className="btn btn-respond btn-accept mr-2">
              {askToPay ? 'Book and pay now' : 'Book now'}
            </button>

            {askToPay && (
              <button onClick={() => this.onSubmit(true)} className="btn btn-respond btn-accept">
                Add to basket
              </button>
            )}
          </>
        )}
      </>
    );
  };

  renderInvitationStatusText = () => {
    const { message } = this.props;
    const { noClubSpacesInfoVisible } = this.state;

    const invitationStatus = propz.get(message, ['invitationStatus'], '');

    const isAuthorisingStatus = invitationStatus === CLUB_PARTICIPATION_MESSAGE_INVITATION_STATUS.AUTHORISING;
    const isPrebookedStatus = invitationStatus === CLUB_PARTICIPATION_MESSAGE_INVITATION_STATUS.PREBOOKED;

    switch (true) {
      case !noClubSpacesInfoVisible: {
        return <div className="mb-4 text-primary">Booking is not available. There are no spaces left.</div>;
      }

      case isPrebookedStatus: {
        return (
          <div className="mb-4 text-primary">
            This item has already been added to your basket. Go to the basket to complete checkout or remove items from
            the basket.
          </div>
        );
      }

      case isAuthorisingStatus: {
        return (
          <div className="mb-4 text-primary">
            You have already started the checkout process for this item. Please complete your payment to book your
            space. Or, you can try booking this item once again a bit later if you have already closed the checkout tab
            in your browser.
          </div>
        );
      }

      default:
        return <div></div>;
    }
  };

  render() {
    const { message, user } = this.props;
    const {
      fields,
      activePriorityIndex,
      selectOptionsForClubPriority,
      activeSelectIndex,
      errors,
      isWaitingListInfoVisible,
      noClubSpacesInfoVisible,
      definition,
      answers,
      formErrors
    } = this.state;

    const askToPay = propz.get(message, ['clubData', 'paymentSettings', 'askToPay'], false);

    const invitationStatus = propz.get(message, ['invitationStatus'], '');

    const isAuthorisingStatus = invitationStatus === CLUB_PARTICIPATION_MESSAGE_INVITATION_STATUS.AUTHORISING;
    const isPrebookedStatus = invitationStatus === CLUB_PARTICIPATION_MESSAGE_INVITATION_STATUS.PREBOOKED;

    const isSameOrderAlreadyExist = isAuthorisingStatus || isPrebookedStatus;

    const isFieldsExist = fields.length > 0;
    const isV2 = message.formSchemaVersion === 2 && !!message.formDefinition;
    const hasV2FormContent = isV2 && (definition.elements || []).length > 0;
    const flowEnabled = isV2 && hasFormFlow(definition);

    const isClubPriorityEnabled = propz.get(user, ['activeSchool', 'isClubPriorityEnabled']);

    const firstName = propz.get(message, ['playerDetailsData', 'firstName']);
    const lastName = propz.get(message, ['playerDetailsData', 'lastName']);
    const fullName = `${firstName} ${lastName}`;

    const deadlineForAnswers = propz.get(message, ['deadlineForAnswers']);
    const isDeadlineExist = !!deadlineForAnswers && moment(deadlineForAnswers).isValid();

    const deadlineM = isDeadlineExist ? moment(deadlineForAnswers) : null;
    const clubEndM = isDeadlineExist ? null : toClubEndMoment(message);

    const cutoffM = deadlineM || clubEndM;
    const isDeadlineArrived = !!cutoffM && moment().isSameOrAfter(cutoffM);

    const isShowActionButtons = !isDeadlineArrived && !isSameOrderAlreadyExist;

    const fieldsUpdated = [...fields];

    return !isSameOrderAlreadyExist && noClubSpacesInfoVisible ? (
      <>
        {(() => {
          const firstName = propz.get(message, ['playerDetailsData', 'firstName'], '');
          const lastName = propz.get(message, ['playerDetailsData', 'lastName'], '');
          const fullName = `${firstName} ${lastName}`.trim();

          const { clubName, venue, runRange, when } = buildClubSummary(message);

          return (
            <>
              <div className="AcceptModal__header">
                <div className="AcceptModal__titles">
                  <h3>Reserve a place?</h3>
                  <p>
                    By booking, <strong>{fullName}</strong> will be added to <strong>{clubName}</strong>.
                  </p>
                </div>
              </div>

              <div className="AcceptModal__summary">
                <div className="AcceptModal__row AcceptModal__row--event">
                  <span className="label">Event</span>
                  <span className="value">{clubName}</span>
                </div>
                {(runRange || when) && (
                  <div className="AcceptModal__chips">
                    {runRange && <span className="AcceptModal__chip">{runRange}</span>}
                    {when && <span className="AcceptModal__chip">{when}</span>}
                  </div>
                )}
                {venue && (
                  <div className="AcceptModal__row">
                    <span className="label">Where</span>
                    <span className="value">{venue}</span>
                  </div>
                )}
              </div>
            </>
          );
        })()}

        {isClubPriorityEnabled && (
          <div>
            <div className={'mClubInvitationFormBold'}>Priority:</div>
            <Select
              onChange={this.onPrioritySelect}
              options={selectOptionsForClubPriority}
              activeResultIndex={activePriorityIndex}
              customClass={'form-control'}
            />
            <div className={'eClubParticipationMessagePriorityCaption'}>
              Please ensure you only select one Priority number per club within the below booking forms, otherwise, the
              school will allocate your child at its discretion.
            </div>
          </div>
        )}

        {hasV2FormContent && (
          <FormRendererV2
            definition={definition}
            answers={answers}
            errors={formErrors}
            onChange={this.onAnswersChange}
            onSubmitRequested={() => this.onSubmit(false)}
            uploadFiles={(files, element) => uploadFormFilesForElement(this.props.user, files, element)}
          />
        )}

        {!isV2 &&
          isFieldsExist &&
          fieldsUpdated.map((field, index) => (
            <div key={`club_invitation_form_field_${index}`} className="mb-3">
              <div className={field.isRequired ? 'font-weight-bold' : ''}>
                {field.heading} {field.isRequired ? '(required)' : ''}
              </div>

              {(field.type === QUESTIONS_TEMPLATE_TYPE.STRING || field.type === QUESTIONS_TEMPLATE_TYPE.NUMBER) && (
                <input
                  type="text"
                  className="form-control"
                  value={field.value}
                  onChange={event => {
                    propz.set(fieldsUpdated[index], ['value'], event.target.value);
                    this.setState({ fields: fieldsUpdated });
                  }}
                />
              )}
              {field.type === QUESTIONS_TEMPLATE_TYPE.BOOLEAN && (
                <Select
                  onChange={value => {
                    const options = getSelectOptionForBooleanQuestion();
                    const nextIndex = options.findIndex(option => option.value === value);
                    const activeSelectIndexUpdated = [...activeSelectIndex];
                    activeSelectIndexUpdated[index] = nextIndex;
                    propz.set(fieldsUpdated[index], ['value'], value);

                    this.setState({
                      activeSelectIndex: activeSelectIndexUpdated
                    });
                  }}
                  options={getSelectOptionForBooleanQuestion()}
                  activeResultIndex={activeSelectIndex[index]}
                  customClass={'form-control'}
                />
              )}
              {field.type === QUESTIONS_TEMPLATE_TYPE.ENUM && (
                <Select
                  onChange={value => {
                    const options = getSelectOptionForClubInvitationEnumQuestion(field.enumOptions);
                    const nextIndex = options.findIndex(option => option.value === value);
                    const activeSelectIndexUpdated = [...activeSelectIndex];
                    activeSelectIndexUpdated[index] = nextIndex;
                    propz.set(fieldsUpdated[index], ['value'], value);

                    this.setState({
                      activeSelectIndex: activeSelectIndexUpdated
                    });
                  }}
                  options={getSelectOptionForClubInvitationEnumQuestion(field.enumOptions)}
                  activeResultIndex={activeSelectIndex[index]}
                  customClass={'form-control'}
                />
              )}
              {errors[index].isError && <div className="alert alert-danger mt-3">{errors[index].errorText}</div>}
            </div>
          ))}

        {isDeadlineArrived ? (
          <div className="AcceptModal__instruction">
            <span className="mClubInvitationFormBold">Unfortunately, booking for this activity has now closed.</span>
          </div>
        ) : (
          <div className="AcceptModal__instruction">
            <span>{`To reserve a place for ${fullName}, click the 'Book now' button below`}</span>
          </div>
        )}

        {isDeadlineExist && !isDeadlineArrived && (
          <div className="ConsentCard__deadlineText mb-3">
            Deadline: <strong>{moment(deadlineForAnswers).format('DD MMM YYYY / HH:mm')}</strong>
          </div>
        )}

        {isWaitingListInfoVisible && (
          <div className="alert alert-danger mt-3">
            {
              'All spaces have been booked already, so you will be added to the waiting list. Once there is a space you will be asked to make a payment for being added to the club.'
            }
          </div>
        )}

        <div className="AcceptModal__cta">
          <button onClick={this.props.closeClubInvitationModal} className="btn btn-light">
            Cancel
          </button>

          {isShowActionButtons && this.props.onDeclineClick && (
            <button onClick={this.props.onDeclineClick} className="btn btn-outline-danger">
              Decline
            </button>
          )}

          {isShowActionButtons && !flowEnabled && this.renderActionButtons()}
        </div>

        {askToPay && (
          <div className="mt-2">
            <p className="text-primary">
              When you proceed to the checkout you will be forwarded to the Stripe secure payment page. Stripe is a
              payment platform that we are partnered with for processing payments. Paying via Stripe means you agree
              with their T&C's and their Privacy Policy, these can be found on their website.
            </p>

            <p className="text-primary">
              Squad In Touch does not have access to or store any payment information associated with you; when you
              agree to save your card details on Stripe's secure payment page you are agreeing to Stripe holding your
              details, not Squad In Touch.
            </p>

            <p className="font-weight-bold">
              It should be noted that as part of your booking a booking fee is included, therefore if for any reason the
              school issues you a refund the booking fee is non-refundable, we thank you for your understanding.
            </p>
          </div>
        )}
      </>
    ) : (
      <>
        {this.renderInvitationStatusText()}

        <button onClick={this.props.closeClubInvitationModal} className="btn btn-secondary mr-2">
          Cancel
        </button>
      </>
    );
  }
}
